import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Trash2 } from "lucide-react";

import { api } from "@/api/client";
import { useRollouts } from "@/api/local/rollout";
import type { Pool, Service } from "@/api/types";
import { useAlert, useConfirm } from "@/components/ConfirmDialog";
import { Pill } from "@/components/Pill";
import { Section } from "@/components/Section";
import { type Focus } from "@/workloads/focus";

interface Props {
  pool: Pool;
  service: Service;
  setFocusAndTab: (focus: Focus, tab: string) => void;
}

/**
 * Pool settings — reduced to the Danger zone (detach this pool from the
 * service). Resource shape / placements / VM image override / per-pool
 * workload override now live on the Spec tab; capacity caps
 * (max_replicas, max_price) live on Autoscaling.
 */
export function PoolSettings({ pool, service, setFocusAndTab }: Props) {
  const queryClient = useQueryClient();
  const rollouts = useRollouts();
  const confirm = useConfirm();
  const alert = useAlert();

  // Pool deletion. Replicas cascade-delete on the controller side
  // (Pool.replicas relationship is `cascade="all, delete-orphan"`), so the
  // reconciler tears down the SkyServe service on its next tick. We
  // gate on the design-system confirm modal because (a) it's a
  // destructive, irreversible op and (b) we want a hard stop while a
  // rollout is touching this pool.
  const remove = useMutation({
    mutationFn: () => api.delete<unknown>(`/pools/${pool.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pools"] });
      queryClient.invalidateQueries({ queryKey: ["pools", "all"] });
      queryClient.invalidateQueries({
        queryKey: ["service", pool.service_id, "pools"],
      });
      // Bounce up to the parent service so we're not staring at a
      // detail page whose underlying row just vanished.
      setFocusAndTab({ type: "service", id: pool.service_id }, "dashboard");
    },
    onError: (err) => {
      void alert({
        title: "Failed to delete pool",
        tone: "danger",
        description:
          err instanceof Error ? err.message : "Failed to delete pool.",
      });
    },
  });

  const rolloutTouchingThisPool =
    rollouts.active != null &&
    rollouts.active.service_id === pool.service_id &&
    (rollouts.active.scope === "service" ||
      rollouts.active.pool_id === pool.id);

  return (
    <Section
      title="Danger zone"
      icon={<AlertTriangle />}
      actions={<Pill tone="danger">irreversible</Pill>}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          justifyContent: "space-between",
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Detach this pool
          </div>
          <p
            className="muted"
            style={{ fontSize: 12, margin: 0, lineHeight: 1.5 }}
          >
            Removes <code>{pool.sky_service_name}</code> from{" "}
            <code>{service.name}</code> and tears down its replicas on
            the next reconciler tick. The service&rsquo;s workload spec
            and other pools are untouched. Cannot be undone.
          </p>
          {rolloutTouchingThisPool && (
            <div className="flash" style={{ marginTop: 12 }}>
              A rollout is currently touching this pool. Wait for it to
              finish or abort it before detaching.
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn--danger"
          onClick={async () => {
            const ok = await confirm({
              title: "Detach pool?",
              tone: "danger",
              confirmLabel: "Detach pool",
              description: (
                <>
                  <p style={{ marginTop: 0 }}>
                    Detach <code className="mono">{pool.sky_service_name}</code>{" "}
                    from <code className="mono">{service.name}</code>? Replicas
                    on this pool will be torn down on the next reconciler tick.
                  </p>
                  <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                    The service&rsquo;s workload spec and other pools are
                    untouched. Cannot be undone.
                  </p>
                </>
              ),
            });
            if (ok) remove.mutate();
          }}
          disabled={rolloutTouchingThisPool || remove.isPending}
          title={
            rolloutTouchingThisPool
              ? "Cannot detach while a rollout is touching this pool"
              : undefined
          }
        >
          <Trash2 />
          {remove.isPending ? "detaching…" : "Detach pool…"}
        </button>
      </div>
    </Section>
  );
}
